import React from "react";
import PropTypes from "prop-types";
import { withStyles } from "@material-ui/core";
import { AdminContextConsumer } from "../pages/Admin"
import Typography from "@material-ui/core/Typography/Typography";
import Card from "@material-ui/core/Card/Card";
import CardContent from "@material-ui/core/CardContent/CardContent";
import gql from 'graphql-tag';
import * as graphql from '../common/graphql';
import Message from "./Message";
import { withIAM } from "../common/iam";
import * as i18n from "../common/i18n";
import ResponsiveContainer from 'recharts/lib/component/ResponsiveContainer';
import {
    Tooltip, PieChart, Pie, Cell, Legend
} from 'recharts';
import Skeleton from "react-loading-skeleton";


const defaultFindingsData = {
    numPointsLow: {
        totalCount: 0
    },
    numPointsHi: {
        totalCount: 0
    },
    mlNull: {
        totalCount: 0
    },
    mlLow: {
        totalCount: 0
    },
    mlHi: {
        totalCount: 0
    },
    distanceLow: {
        totalCount: 0
    },
    distanceHi: {
        totalCount: 0
    },
    distanceMaxNull: {
        totalCount: 0
    },
    distanceMinNull: {
        totalCount: 0
    },
    varianceHi: {
        totalCount: 0
    },
    varianceLow: {
        totalCount: 0
    },
    testFlushes: {
        totalCount: 0
    }
}

const styles = theme => ({
    root: {
        flexGrow: 1,
    },
    head: {
        display: "flex",
        justifyContent: "center",
        alignItems: "baseline"
    },
    header: {
        margin: theme.spacing(5)
    },
    formControl: {
        margin: theme.spacing(),
        minWidth: 120,
    },
    selectEmpty: {
        marginTop: theme.spacing(2),
    },
});

const COLORS = ['#0056FE', '#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#117A65', '#F4D03F', '#8E44AD', '#E74C3C', '#BDC3C7', '#EC7063', '#0E6655', '#5D6D7E', '#4b5d67'];

const RADIAN = Math.PI / 180;

const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, index }) => {
    if (percent * 100 > 10) {
        const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
        const x = cx + radius * Math.cos(-midAngle * RADIAN);
        const y = cy + radius * Math.sin(-midAngle * RADIAN);

        return (
            <text x={x} y={y} fill="white" textAnchor={x > cx ? 'start' : 'end'} dominantBaseline="central">
                {`${(percent * 100).toFixed(0)}%`}
            </text>
        );
    } else {
        return null
    }
};

const FindingsChart = (props) => {


    const { findingsData } = props;

    const data = [
        { name: 'Nur Testspülungen', Anzahl: findingsData.testFlushes.totalCount },
        { name: 'zu wenig Messpunkte', Anzahl: findingsData.numPointsLow.totalCount },
        { name: 'zu viele Messpunkte', Anzahl: findingsData.numPointsHi.totalCount },
        { name: 'kein Durchfluss', Anzahl: findingsData.mlNull.totalCount },
        { name: 'zu geringer Durchfluss', Anzahl: findingsData.mlLow.totalCount },
        { name: 'zu hoher Durchfluss', Anzahl: findingsData.mlHi.totalCount },
        { name: 'Abstand zu gering', Anzahl: findingsData.distanceLow.totalCount },
        { name: 'Abstand zu hoch', Anzahl: findingsData.distanceHi.totalCount },
        { name: 'max Abstand 0', Anzahl: findingsData.distanceMaxNull.totalCount },
        { name: 'min Abstand 0', Anzahl: findingsData.distanceMinNull.totalCount },
        { name: 'Abweichungen nach oben', Anzahl: findingsData.varianceHi.totalCount },
        { name: 'Abweichungen nach unten', Anzahl: findingsData.varianceLow.totalCount },
    ];

    return (

        <ResponsiveContainer width="99%" height={320}>
            <PieChart width={320} height={320}>
                <Pie
                    data={data}
                    cx={150}
                    cy={200}
                    labelLine={false}
                    label={renderCustomizedLabel}
                    legend={true}
                    outerRadius={100}
                    fill="#8884d8"
                    dataKey="Anzahl"
                >
                    {
                        data.map((entry, index) => <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />)
                    }
                </Pie>
                <Legend
                    verticalAlign="bottom"
                    align="left"
                    layout="vertical"
                    wrapperStyle={{
                        backgroundColor: '#f5f5f5',
                        border: '1px solid #d5d5d5',
                        borderRadius: 3,
                    }}
                    payload={data.map((item, index) => ({
                        id: item.name,
                        type: "square",
                        value: `${item.name}: ${item.Anzahl} ${item.Anzahl === 1 ? 'Messung' : 'Messungen'}`,
                        color: COLORS[index % COLORS.length],
                    }))}
                />
                <Tooltip />
            </PieChart>
        </ResponsiveContainer>
    );
}

class DeviceFindingsReport extends React.Component {

    constructor(props) {
        super(props);

        this.state = {
            loading: true,
            findingsChartLoading: true,
            findingsData: defaultFindingsData,
            message: null,
            details: null,
            hideAfter: 3000,
            variant: "info",
        };
    }

    showMessage = (message, variant, details, hideAfter) => {
        this.setState({
            message: message,
            variant: variant ? variant : "info",
            hideAfter: hideAfter ? hideAfter : 3000,
            details: details ? details : null,
        })
    }

    closeMessage = () => {
        this.setState({
            message: null,
            details: null,
            hideAfter: 3000,
            variant: "info",
        })
    }

    fetchFindings = async () => {
        try {
            const result = await graphql.clientWithToken(this.props.accessToken).query({
                query: gql`
            query {
                total: findings(subject: DEVICES) {
                    totalCount
                }
                testFlushes: findings(
                    subject: DEVICES
                    where: [
                    {
                        column: "type"
                        comparator: IS
                        string: "TESTFLUSHES_ONLY"
                    }
                    ]
                ) {
                    totalCount
                }
                numPointsLow: findings(
                    subject: DEVICES
                    where: [
                    {
                        column: "type"
                        comparator: IS
                        string: "UNPLAUSIBLE_LASTEST_MEASUREMENT_POINTS_LO"
                    }
                    ]
                ) {
                    totalCount
                }
                numPointsHi: findings(
                    subject: DEVICES
                    where: [
                    {
                        column: "type"
                        comparator: IS
                        string: "UNPLAUSIBLE_LASTEST_MEASUREMENT_POINTS_HI"
                    }
                    ]
                ) {
                    totalCount
                }
                mlNull: findings(
                    subject: DEVICES
                    where: [
                    {
                        column: "type"
                        comparator: IS
                        string: "UNPLAUSIBLE_LASTEST_MEASUREMENT_ML_NULL"
                    }
                    ]
                ) {
                    totalCount
                }
                mlLow: findings(
                    subject: DEVICES
                    where: [
                    {
                        column: "type"
                        comparator: IS
                        string: "UNPLAUSIBLE_LASTEST_MEASUREMENT_ML_LO"
                    }
                    ]
                ) {
                    totalCount
                }
                    mlHi: findings(
                    subject: DEVICES
                    where: [
                    {
                        column: "type"
                        comparator: IS
                        string: "UNPLAUSIBLE_LASTEST_MEASUREMENT_ML_HI"
                    }
                    ]
                ) {
                    totalCount
                }
                distanceLow: findings(
                    subject: DEVICES
                    where: [
                    {
                        column: "type"
                        comparator: IS
                        string: "UNPLAUSIBLE_LASTEST_MEASUREMENT_DISTANCE_LO"
                    }
                    ]
                ) {
                    totalCount
                }
                distanceHi: findings(
                    subject: DEVICES
                    where: [
                    {
                        column: "type"
                        comparator: IS
                        string: "UNPLAUSIBLE_LASTEST_MEASUREMENT_DISTANCE_HI"
                    }
                    ]
                ) {
                    totalCount
                }
                distanceMaxNull: findings(
                    subject: DEVICES
                    where: [
                    {
                        column: "type"
                        comparator: IS
                        string: "UNPLAUSIBLE_LASTEST_MEASUREMENT_MAX_DISTANCE_NULL"
                    }
                    ]
                ) {
                    totalCount
                }
                    distanceMinNull: findings(
                    subject: DEVICES
                    where: [
                    {
                        column: "type"
                        comparator: IS
                        string: "UNPLAUSIBLE_LASTEST_MEASUREMENT_MIN_DISTANCE_NULL"
                    }
                    ]
                ) {
                    totalCount
                }
                varianceHi: findings(
                    subject: DEVICES
                    where: [
                    {
                        column: "type"
                        comparator: IS
                        string: "UNPLAUSIBLE_LASTEST_MEASUREMENT_VARIANCE_HI"
                    }
                    ]
                ) {
                    totalCount
                }
                varianceLow: findings(
                    subject: DEVICES
                    where: [
                    {
                        column: "type"
                        comparator: IS
                        string: "UNPLAUSIBLE_LASTEST_MEASUREMENT_VARIANCE_LO"
                    }
                    ]
                ) {
                    totalCount
                }
                }
                `,
                fetchPolicy: "no-cache"
            });

            const searchResult = {
                data: result.data
            }

            this.handleFindingsChange(searchResult);
        }
        catch (error) {
            this.showMessage(error.message, "error");
        }
    }

    handleFindingsChange = async (findingCounters) => {
        this.setState({
            findingsData: findingCounters.data,
            findingsChartLoading: false
        })

    }

    componentDidMount() {
        this.setState({
            loading: false
        });
        this.fetchFindings()
    }

    render() {

        const { classes } = this.props;

        return (
            <React.Fragment>
                {this.state.loading ? <div /> : (
                    <AdminContextConsumer>
                        {({ onLink }) => (
                            <React.Fragment>
                                <Card>
                                    <CardContent>

                                        <Typography variant="h5" component="h2">
                                            {i18n.get("devicesFindingsReport.h2")}
                                        </Typography>

                                        {this.state.findingsChartLoading ?
                                            <section>
                                                <Skeleton height={220} width={308} style={{ marginTop: '90px', verticalAlign: 'bottom' }} />
                                                <Skeleton circle={true} height={200} width={200} style={{ marginLeft: '35px' }} />
                                            </section>
                                            :
                                            <FindingsChart findingsData={this.state.findingsData} />
                                        }

                                    </CardContent>
                                </Card>
                            </React.Fragment>
                        )}
                    </AdminContextConsumer>
                )}

                <Message
                    hideAfter={this.state.hideAfter}
                    onClose={this.closeMessage}
                    variant={this.state.variant}
                    message={this.state.message}
                    details={this.state.details}
                    open={this.state.message !== null}
                />

            </React.Fragment>
        )
    }
}

DeviceFindingsReport.propTypes = {
    classes: PropTypes.object.isRequired,
};

export default withIAM(withStyles(styles, { withTheme: true })(DeviceFindingsReport))